import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { watch, ref, computed, onMounted } from 'vue'
import { api } from '@/composables/api'

interface FormData {
  equipment_type: string
  quantity: number
  manufacturer: string
  model: string
  rating: number | null
  screen: string
  cpu: string
  ram: string
  storage: string
  gpu: string
  os: string
  charger: string
  status: string
  location: string
  asset_tag: string
  comments: string
  value: number | null
  donor: string
  donated_on: string
}

interface School {
  id: string
  _id?: string
  name: string
}

interface Props {
  show: boolean
  isEditing: boolean
  formData: FormData
  submitting: boolean
  error: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'EquipmentModal',
  props: {
    show: { type: Boolean },
    isEditing: { type: Boolean },
    formData: {},
    submitting: { type: Boolean },
    error: {}
  },
  emits: ['close', 'submit'],
  setup(__props: any, { emit: __emit }) {

/* eslint-disable no-undef */
const props = __props
const emit = __emit

const formRef = ref<any>(null)
const localFormData = ref<FormData>({ ...props.formData })
const schools = ref<School[]>([])
const donorOptions = ref<string[]>([])
const chargerOptions = ref<string[]>([])

const showModal = computed({
  get: () => props.show,
  set: (value) => {
    if (!value) emit('close')
  }
})

const equipmentTypes = [
  { title: 'Laptop', value: 'laptop' },
  { title: 'Desktop', value: 'desktop' },
  { title: 'Monitor', value: 'monitor' },
  { title: 'Tablet', value: 'tablet' },
  { title: 'Other', value: 'other' }
]

const statusOptions = [
  { title: 'Processing', value: 'processing' },
  { title: 'Troubleshooting', value: 'troubleshooting' },
  { title: 'Parts', value: 'parts' },
  { title: 'Available', value: 'available' },
  { title: 'Claimed', value: 'claimed' },
  { title: 'Delivered', value: 'delivered' },
  { title: 'Retired', value: 'retired' }
]

const osOptions = [
  'Windows',
  'macOS',
  'Chrome OS',
  'Linux',
  'Linux (Mint)',
  'Linux (Zorin)',
  'Linux (Ubuntu)'
]

const locationOptions = computed(() => {
  const options = [{ title: 'SCS', value: 'SCS' }]

  // Add schools to location options
  schools.value.forEach(school => {
    options.push({
      title: school.name,
      value: school.name
    })
  })

  return options
})

const loadSchools = async () => {
  try {
    const response = await api.get('/schools')
    schools.value = response.data || response || []
  } catch (error) {
    console.error('Error loading schools:', error)
    schools.value = []
  }
}

const loadDonors = async () => {
  try {
    const response = await api.get('/equipment/donors/list')
    const donors = response.data || response || []
    donorOptions.value = donors.map((d: any) => d.donor).filter(Boolean)
  } catch (error) {
    console.error('Error loading donors:', error)
    donorOptions.value = []
  }
}

const loadChargers = async () => {
  try {
    const response = await api.get('/equipment/chargers/list')
    const chargers = response.data || response || []
    chargerOptions.value = chargers.map((c: any) => c.charger).filter(Boolean)
  } catch (error) {
    console.error('Error loading chargers:', error)
    chargerOptions.value = []
  }
}

const closeModal = () => {
  emit('close')
}

const submitForm = async () => {
  const { valid } = await formRef.value.validate()

  if (valid) {
    emit('submit', localFormData.value)
  }
}

watch(() => props.formData, (newVal) => {
  localFormData.value = { ...newVal }
}, { deep: true })

onMounted(() => {
  loadSchools()
  loadDonors()
  loadChargers()
})

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_combobox = _resolveComponent("v-combobox")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((showModal).value = $event)),
    "max-width": "800px",
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "modal-header" }, {
            default: _withCtx(() => [
              _createElementVNode("span", null, _toDisplayString(__props.isEditing ? 'Edit Equipment' : 'Add Equipment'), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_text, { class: "pt-4" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_form, {
                ref_key: "formRef",
                ref: formRef
              }, {
                default: _withCtx(() => [
                  (__props.error)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 0,
                        type: "error",
                        variant: "tonal",
                        class: "mb-4"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(__props.error), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_select, {
                            modelValue: localFormData.value.equipment_type,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localFormData.value.equipment_type) = $event)),
                            items: equipmentTypes,
                            label: "Equipment Type",
                            variant: "outlined",
                            rules: [v => !!v || 'Equipment type is required'],
                            required: ""
                          }, null, 8, ["modelValue", "rules"])
                        ]),
                        _: 1
                      }),
                      (!__props.isEditing)
                        ? (_openBlock(), _createBlock(_component_v_col, {
                            key: 0,
                            cols: "12",
                            sm: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: localFormData.value.quantity,
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localFormData.value.quantity) = $event)),
                                modelModifiers: { number: true },
                                label: "Quantity",
                                type: "number",
                                min: "1",
                                variant: "outlined",
                                rules: [v => !!v || 'Quantity is required'],
                                required: ""
                              }, null, 8, ["modelValue", "rules"])
                            ]),
                            _: 1
                          }))
                        : (_openBlock(), _createBlock(_component_v_col, {
                            key: 1,
                            cols: "12",
                            sm: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_select, {
                                modelValue: localFormData.value.status,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localFormData.value.status) = $event)),
                                items: statusOptions,
                                label: "Status",
                                variant: "outlined"
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          }))
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.manufacturer,
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((localFormData.value.manufacturer) = $event)),
                            label: "Manufacturer",
                            variant: "outlined",
                            placeholder: "e.g., Dell, HP, Lenovo"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.model,
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((localFormData.value.model) = $event)),
                            label: "Model",
                            variant: "outlined",
                            placeholder: "Model name/number"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.rating,
                            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((localFormData.value.rating) = $event)),
                            modelModifiers: { number: true },
                            label: "Rating (1-10)",
                            type: "number",
                            min: "1",
                            max: "10",
                            variant: "outlined",
                            placeholder: "Rate 1-10"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.screen,
                            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((localFormData.value.screen) = $event)),
                            label: "Screen",
                            variant: "outlined",
                            placeholder: "e.g., 15.6 inch"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.cpu,
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((localFormData.value.cpu) = $event)),
                            label: "CPU",
                            variant: "outlined",
                            placeholder: "e.g., Intel i5"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.ram,
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((localFormData.value.ram) = $event)),
                            label: "RAM",
                            variant: "outlined",
                            placeholder: "e.g., 8GB"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.storage,
                            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((localFormData.value.storage) = $event)),
                            label: "Storage",
                            variant: "outlined",
                            placeholder: "e.g., 256GB SSD"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.gpu,
                            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((localFormData.value.gpu) = $event)),
                            label: "GPU",
                            variant: "outlined",
                            placeholder: "e.g., Intel UHD"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_combobox, {
                            modelValue: localFormData.value.os,
                            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((localFormData.value.os) = $event)),
                            items: osOptions,
                            label: "Operating System",
                            variant: "outlined",
                            placeholder: "Select or type OS"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_select, {
                            modelValue: localFormData.value.location,
                            "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((localFormData.value.location) = $event)),
                            items: locationOptions.value,
                            label: "Location",
                            variant: "outlined"
                          }, null, 8, ["modelValue", "items"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_combobox, {
                            modelValue: localFormData.value.charger,
                            "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((localFormData.value.charger) = $event)),
                            items: chargerOptions.value,
                            label: "Charger",
                            variant: "outlined",
                            placeholder: "Select or type charger",
                            hint: "e.g. USB-C 65W, Barrel 90W. Copied when adding more of this model.",
                            "persistent-hint": "",
                            clearable: ""
                          }, null, 8, ["modelValue", "items"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.value,
                            "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((localFormData.value.value) = $event)),
                            modelModifiers: { number: true },
                            label: "Value ($)",
                            type: "number",
                            min: "0",
                            step: "0.01",
                            variant: "outlined",
                            prefix: "$",
                            placeholder: "e.g., 250.00",
                            hint: "Used on donation receipts. Copied when adding more of this model.",
                            "persistent-hint": ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_divider, { class: "my-4" }),
                  _cache[20] || (_cache[20] = _createElementVNode("div", { class: "section-label mb-3" }, "Donation Info", -1)),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_combobox, {
                            modelValue: localFormData.value.donor,
                            "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((localFormData.value.donor) = $event)),
                            items: donorOptions.value,
                            label: "Donor",
                            variant: "outlined",
                            placeholder: "Person or organization",
                            hint: "Not copied when adding more of this model unless you specify it",
                            "persistent-hint": "",
                            clearable: ""
                          }, null, 8, ["modelValue", "items"])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_v_col, {
                        cols: "12",
                        sm: "6"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_text_field, {
                            modelValue: localFormData.value.donated_on,
                            "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((localFormData.value.donated_on) = $event)),
                            label: "Donation Date",
                            type: "date",
                            variant: "outlined",
                            hint: "Printed on the donation receipt",
                            "persistent-hint": "",
                            clearable: ""
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_divider, { class: "my-4" }),
                  (__props.isEditing && localFormData.value.asset_tag)
                    ? (_openBlock(), _createBlock(_component_v_row, { key: 1 }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, { cols: "12" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: localFormData.value.asset_tag,
                                "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((localFormData.value.asset_tag) = $event)),
                                label: "Asset Tag",
                                variant: "outlined",
                                readonly: "",
                                hint: "Asset tag is auto-generated",
                                "persistent-hint": ""
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  _createVNode(_component_v_row, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_col, { cols: "12" }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_textarea, {
                            modelValue: localFormData.value.comments,
                            "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((localFormData.value.comments) = $event)),
                            label: "Comments",
                            variant: "outlined",
                            rows: "3",
                            placeholder: "Additional notes..."
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 512)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_actions, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_spacer),
              _createVNode(_component_v_btn, {
                color: "grey",
                onClick: closeModal,
                disabled: __props.submitting
              }, {
                default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                  _createTextVNode(" Cancel ", -1)
                ]))]),
                _: 1
              }, 8, ["disabled"]),
              _createVNode(_component_v_btn, {
                color: "primary",
                onClick: submitForm,
                loading: __props.submitting
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(__props.isEditing ? 'Update' : 'Add'), 1)
                ]),
                _: 1
              }, 8, ["loading"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})